import { useState } from 'react';
import { tokens } from './tokens';

type Props = Readonly<{
  name: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  maxLength?: number;
  error?: string;
}>;

function resolveBorderColor(error: string | undefined, focused: boolean): string {
  // An edge a person must see to find the field: 3:1 against the surface (WCAG 1.4.11).
  if (error) return tokens.errorText;
  if (focused) return tokens.goldOnLight;
  return tokens.controlBorder;
}

/**
 * Opinionated multi-line input. Always 5 rows tall, always shows a live
 * character counter when maxLength is set. No `rows`, no `resize` knob.
 */
export function FormTextArea({
  name,
  label,
  value,
  onChange,
  placeholder,
  required = false,
  disabled = false,
  maxLength,
  error,
}: Props): JSX.Element {
  const [focused, setFocused] = useState(false);
  const borderColor = resolveBorderColor(error, focused);

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label
          htmlFor={name}
          className="block text-xs font-semibold uppercase tracking-wide"
          style={{ color: tokens.textLabel }}
        >
          {label}
          {required && <span style={{ color: tokens.errorText }}> *</span>}
        </label>
        {maxLength !== undefined && (
          <span className="text-xs" style={{ color: tokens.textPlaceholder }}>
            {value.length}/{maxLength}
          </span>
        )}
      </div>
      <textarea
        id={name}
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        maxLength={maxLength}
        rows={5}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${name}-error` : undefined}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        className="w-full px-4 py-2.5 text-sm rounded-xl outline-none transition-all resize-none"
        style={{
          background: disabled ? tokens.disabledBg : tokens.surfaceMuted,
          border: `1.5px solid ${borderColor}`,
          color: tokens.textPrimary,
          fontFamily: 'inherit',
        }}
      />
      {error && (
        <p id={`${name}-error`} className="mt-1.5 text-xs" style={{ color: tokens.errorText }}>
          {error}
        </p>
      )}
    </div>
  );
}
